import { ComponentProps, ReactNode } from 'react'
import { useFormikContext } from 'formik'
import { AnySchema } from 'yup'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'

import CollectionSection, {
  CollectionRecord,
  FieldsGrid,
} from '../components/collection-section'
import NotesField from '../components/notes-field'
import SectionTotals from '../components/section-totals'
import useSectionToggle from '../use-section-toggle'

export type RecordCollection = {
  items: CollectionRecord[]
  createItem: (attributes?: Record<string, unknown>) => Promise<unknown>
  updateItem: (id: number, attributes: Record<string, unknown>) => unknown
  deleteItem: (id: number) => unknown
  isCreating: boolean
}

type HandleFieldSave = (event: {
  name: string
  value: string | number | null
}) => void

export type RenderRecordFields = (fieldProps: {
  handleSave: HandleFieldSave
  values: Record<string, unknown>
  // fields that pair up side by side in a full-width card stack in a
  // half-width one, where two would be too cramped
  pairedFieldSpan: 6 | 12
}) => ReactNode

type RowProps = {
  record: CollectionRecord
  schema: AnySchema
  updateItem: RecordCollection['updateItem']
  renderFields: RenderRecordFields
  isFullWidth: boolean
}

const RecordFields = ({
  record,
  schema,
  updateItem,
  renderFields,
  isFullWidth,
}: RowProps) => {
  const { values } = useFormikContext<Record<string, unknown>>()

  const handleSave = useValidatedSave({
    schema,
    save: ({ name, value }) => updateItem(record.id, { [name]: value }),
  })

  return (
    <>
      <FieldsGrid gap="S">
        {renderFields({
          handleSave,
          values,
          pairedFieldSpan: isFullWidth ? 6 : 12,
        })}
      </FieldsGrid>
      <NotesField name="notes" handleSave={handleSave} />
    </>
  )
}

const RecordForm = withForm(RecordFields)

type Props = {
  heading: string
  cardType: string
  addLabel: string
  toggleName: string
  sectionActive: boolean | undefined
  saveProbateEstateField: (event: { name: string; value: unknown }) => void
  collection: RecordCollection
  schema: AnySchema
  totals?: ComponentProps<typeof SectionTotals>
  renderFields: RenderRecordFields
}

export type RecordsSubSectionProps = Pick<
  Props,
  'collection' | 'sectionActive' | 'saveProbateEstateField'
>

type ListProps = Partial<Pick<Props, 'heading'>> &
  Pick<
    Props,
    | 'cardType'
    | 'addLabel'
    | 'collection'
    | 'schema'
    | 'totals'
    | 'renderFields'
  > &
  Pick<ComponentProps<typeof CollectionSection>, 'toggle'>

export const RecordsList = ({
  heading,
  cardType,
  addLabel,
  collection,
  schema,
  totals,
  renderFields,
  toggle,
}: ListProps) => (
  <CollectionSection
    heading={heading}
    type={cardType}
    records={collection.items}
    isAdding={collection.isCreating}
    onAdd={() => collection.createItem()}
    addLabel={addLabel}
    onDelete={collection.deleteItem}
    toggle={toggle}
    summary={totals && <SectionTotals {...totals} />}
    renderRow={(record, { isFullWidth }) => (
      <RecordForm
        initialValues={record.attributes}
        validationSchema={schema}
        record={record}
        schema={schema}
        updateItem={collection.updateItem}
        renderFields={renderFields}
        isFullWidth={isFullWidth}
      />
    )}
  />
)

const RecordsSection = ({
  toggleName,
  sectionActive,
  saveProbateEstateField,
  ...listProps
}: Props) => {
  const toggle = useSectionToggle({
    name: toggleName,
    initialValue: sectionActive,
    save: saveProbateEstateField,
  })

  return <RecordsList {...listProps} toggle={toggle} />
}

export default RecordsSection
